/**
 * Wave 1 operational widget — Today's Arrivals & Departures with
 * one-click check-in/check-out. Ported from legacy HTHD DashboardPage.tsx
 * lines 414-628 per outputs/audits/platform-vs-legacy-parity-2026-05-11.md
 * Gap A.
 *
 * UX:
 *  - Desktop: two columns (Arrivals left, Departures right)
 *  - Mobile: tab-switch (legacy did the same — better for 375px viewports)
 *  - Arrival rows have a one-click "Check In" button.
 *  - Departure rows DEEP-LINK to the booking's row on the Bookings page
 *    (#251 should-fix 5): check-out is a MONEY action — package
 *    redemption, open-invoice record, settle-now collection — and the
 *    Bookings page owns that orchestration. The widget's old one-click
 *    POST bypassed the entire money path (visit silently exited the
 *    funnel), so it routes to /bookings?focus=<bookingId> instead.
 *
 * Server contracts:
 *  - GET  /api/admin/dashboard/today — fetched ONCE by the Dashboard
 *    page (spec D1, 2026-07-09 wave) and passed down, so this list and
 *    the FacilityHero's arrivals/departures tiles can never disagree
 *    (Principle 1: numbers that could disagree come from one source).
 *  - POST /api/bookings/admin/:id/check-in  (existing — already wired in api.ts)
 *
 * Path-naming note: the prompt spec called for
 * `POST /api/admin/bookings/:id/check-in` but the existing platform
 * endpoint lives at `/api/bookings/admin/:id/check-in` (the booking
 * module mount). We use the existing endpoint to avoid duplicating
 * routes — the AC behavior is unchanged.
 *
 * Data honesty (spec D2, Principle 5): when the page-level data-status
 * probe says today's zeros are structural (`dataSuspect`), the empty
 * states switch to honest copy ("Today's schedule isn't in this system
 * yet") instead of a cheerful "No arrivals scheduled".
 *
 * Ted #2 (#905) — household check-in. Arrivals that share a `customerId`
 * render under ONE household header ("Hazel, Hank & Skipper — Jodie
 * Royer") with a single "Check in all (3)" button beside the per-row
 * buttons. The bulk action is N calls to the SAME check-in POST — no new
 * route — settled together so one dog's refusal never stops the siblings,
 * followed by ONE refresh. Grouping is by customer id only (never by
 * name); rows without an id keep today's single button. Helpers live in
 * ./household-checkin.ts and are shared with the Front Desk page.
 *
 * Ted #23 (#984) — SERVICE LEGS. An added service (nail trim, bath, walk) is
 * its own booking row on `/today` (`isLeg`, `parentBookingId`, `addOnName`),
 * and it has no lifecycle at the desk: the server cascades its visit's
 * check-in and check-out to it. So, exactly as the Front Desk page does: a
 * leg row stays VISIBLE and TAGGED and reads as the add-on; where a lifecycle
 * button would be it says "Checks in / out with the visit" (static text, not
 * a disabled button); a household is a family of VISITS, so a leg never
 * makes one, never widens its "Check in all (N)" and is never POSTed by it;
 * and every count on this widget is `countVisits`, not rows. Before this,
 * Milo's visit plus one nail trim read "Arrivals 2", offered "Check in all
 * (2)" for one dog, and the bulk tap POSTed the leg's check-in on its own.
 *
 * Ted #32c (#1158, #1160) — the Arrivals row carries the reservation's own
 * actions (Edit, No-show, Cancel) beside Check In, through the SAME dialogs the
 * Bookings page uses (`useReservationActions`); and Check In on a dog whose
 * family has other dogs still to arrive opens the shared picker with ONLY the
 * tapped dog going in and every sibling unticked. "Check in all (N)" opens the
 * same picker with the family ticked — it no longer posts without asking.
 *
 * Sawyer, 10-30: "There should be a check in box next to the arriving dogs on
 * the dashboard." There always was, on every pending and confirmed row at every
 * width and for every desk role: it sat two screens down, inside a 360 px box
 * showing two of the day's buttons. So the widget now sits above the rollup
 * (Dashboard.tsx), the hero jumps to it, the box shows dogs instead of a slot,
 * and the row wraps the button under the dog when the column is narrow instead
 * of squeezing the dog's name. Same `requestCheckIn`, so no gate moved. Departure
 * rows carry "Run card": with daycare off the prompt, it is the way to that card.
 *
 * Styling: `ferro-*` BEM classes defined in apps/admin-app/src/
 * index.css (admin-app is not on Tailwind — see the css file's existing
 * `ferro-staff-table-view` block for the same caveat).
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { checkOutPath } from '../checkout/check-out-path.js';
import { checkInBooking, isArrivalUnverified, todayIsoDate } from '../../lib/api.js';
import { DogFirstCell, useArrivalVerification } from '../dog-first/index.js';
import { OwnerLink } from '../owner-link/index.js';
// Ted #32c (#1158) — Cancel / Edit / No-show on the Arrivals row.
import { ExpectedRowActions } from '../booking/ExpectedRowActions.js';
import { reservationTargetFromArrival } from '../booking/reservation-target.js';
import { useReservationActions } from '../booking/useReservationActions.js';
// Ted #32c (#1160) — the one family picker, siblings unticked by default.
import { FamilyCheckInDialog } from '../check-in/FamilyCheckInDialog.js';
import { useRunCardPrompt } from '../run-cards/useRunCardPrompt.js';
// Sawyer 11 client-notes-agreements (F1)
import { ClientNoteChip, ClientNoteSignalsProvider } from '../customers/client-note-signals.js';
import { familyMemberFromArrival, siblingsStillToArrive } from '../check-in/family-check-in.js';
// Ted 09-24 / 09-29 (lane LC) — "Before you check in", the same step the
// Front Desk and Bookings mount; asked from the row's flags before the POST.
import { useCheckInInterrupt } from '../check-in/useCheckInInterrupt.js';
import {
  describeStopped,
  planCheckInInterrupt,
  type InterruptDecision,
} from '../check-in/check-in-interrupt.js';

/** Lane LC — what a re-run of `handleCheckIn` already knows from the step. */
interface CheckInOpts {
  heatConfirmed?: boolean;
  interrupted?: boolean;
}
import {
  formatTime,
  type DashboardArrival,
  type DashboardDeparture,
  type DashboardTodayResponse,
} from './dashboard-api.js';
import { serviceColorClass } from '@ferro/core/modules/tenant-settings/service-colors.js';
import '../../service-colors.css';
import {
  checkInHousehold,
  householdDogNames,
  joinDogNames,
  segmentByHousehold,
  summarizeHouseholdCheckIn,
} from './household-checkin.js';
import { countVisits, serviceRowLabel } from './service-legs.js';
// Sawyer 03 today-notes-board (C2, G8) — the visit's notes, read-only here.
import { BookingNoteCallout, factsFromArrival, useOwnerNotesPrompt } from '../booking-notes/index.js';
import { deskNotesOf } from './dashboard-api.js';

interface ArrivalsDeparturesProps {
  apiUrl: string;
  token: string;
  /** Today's payload — owned by the Dashboard page (one source). */
  data: DashboardTodayResponse | null;
  /** True while the FIRST payload is loading (skeleton state). */
  isLoading: boolean;
  error: string | null;
  /** Recovery action for the error state (refetches /today). */
  onRetry?: () => void;
  /** Principle 5 — today's zeros are structural; honest empty copy. */
  dataSuspect?: boolean;
  /** Called after a successful check-in so the parent can refresh. */
  onMutation?: () => void;
  /**
   * Ted Item 34 — offer the run card after a check-in succeeds. The page
   * passes `config.features?.runCardPromptOnCheckIn !== false`; default on.
   */
  runCardPrompt?: boolean;
}

type ActiveTab = 'arrivals' | 'departures';

export function ArrivalsDepartures({
  apiUrl,
  token,
  data,
  isLoading,
  error,
  onRetry,
  dataSuspect = false,
  onMutation,
  runCardPrompt = true,
}: ArrivalsDeparturesProps): JSX.Element {
  // Ted Item 34 — the run card, offered the moment a check-in succeeds.
  const runCards = useRunCardPrompt({ apiUrl, token, enabled: runCardPrompt });
  const offerRunCards = runCards.offer;
  // Ted #2 (#905) — a Set, not a single id: a household check-in puts every
  // row in the group into its loading state at once.
  const [actionLoading, setActionLoading] = useState<Set<string>>(() => new Set());
  const [actionError, setActionError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>('arrivals');
  const navigate = useNavigate();

  // Ted #32c (#1158) — Cancel / Edit / No-show on the row. The parent owns
  // /today, so a change just asks it to refetch.
  const reservation = useReservationActions({
    apiUrl,
    onChanged: () => onMutation?.(),
  });
  // Ted #32c (#1160) — the family picker: `anchor` is the dog whose Check In
  // was tapped (null for "Check in all"), `siblings` the others still to arrive.
  const [family, setFamily] = useState<{
    anchor: DashboardArrival | null;
    siblings: DashboardArrival[];
    startTicked: 'anchor-only' | 'all';
  } | null>(null);

  // Ted #28 (#1050) — the arrival-verify step, shared with Front Desk and
  // Bookings. The server refuses a transported dog's check-in until a human
  // confirms it is here; the queue asks one dog at a time and re-runs the
  // check-in with the answer through `checkInRef` (no dependency cycle).
  const { ask: askArrival, dialog: arrivalDialog } = useArrivalVerification();
  const checkInRef = useRef<(bookingId: string, arrivalVerified?: boolean, opts?: CheckInOpts) => Promise<void>>();
  const { ask: askInterrupt, dialog: interruptDialog } = useCheckInInterrupt();
  // Sawyer #1296 — "Add this to the owner's notes?" after a check-in; waits
  // behind every other dialog here (never modal-on-modal).
  const ownerNotes = useOwnerNotesPrompt({
    apiUrl,
    onAdded: () => onMutation?.(),
    blocked: runCards.active || family !== null || arrivalDialog !== null || interruptDialog !== null || reservation.dialogs !== null,
  });
  const offerOwnerNotes = ownerNotes.offer;

  const setLoading = useCallback((bookingIds: ReadonlyArray<string>, on: boolean) => {
    setActionLoading((prev) => {
      const next = new Set(prev);
      for (const id of bookingIds) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);

  const handleCheckIn = useCallback(
    async (bookingId: string, arrivalVerified?: boolean, opts: CheckInOpts = {}) => {
      // Lane LC — "Before you check in", from the row's own flags, before the POST.
      if (!opts.interrupted && !arrivalVerified) {
        const item = (data?.arrivals ?? []).find((a) => a.bookingId === bookingId);
        const plan = planCheckInInterrupt(item ? [item] : []);
        if (plan.needed) {
          askInterrupt({
            key: bookingId,
            plan,
            onProceed: (result) => {
              const stopped = describeStopped(result.stopped);
              if (stopped) {
                setActionError(stopped);
                setTimeout(() => setActionError(null), 8000);
              }
              for (const d of result.proceed) {
                void checkInRef.current?.(d.bookingId, d.arrivalVerified, { heatConfirmed: d.heatConfirmed, interrupted: true });
              }
            },
          });
          return;
        }
      }
      setLoading([bookingId], true);
      setActionError(null);
      try {
        await checkInBooking(apiUrl, token, bookingId, undefined, arrivalVerified, opts.heatConfirmed);
        // The parent owns /today — its refresh bump flows new data down.
        onMutation?.();
        offerRunCards([bookingId]);
        // Sawyer #1296 — asked only after the check-in landed.
        const checkedIn = (data?.arrivals ?? []).find((a) => a.bookingId === bookingId);
        if (checkedIn) offerOwnerNotes([factsFromArrival(checkedIn)]);
      } catch (err) {
        // Ted #28 (#1050) — the dog arrives by transport and nobody has said
        // it is here yet. Ask, then re-run with the answer; the row stays on
        // "Check In" meanwhile, which is the honest state.
        if (isArrivalUnverified(err)) {
          const item = (data?.arrivals ?? []).find((a) => a.bookingId === bookingId);
          askArrival({
            bookingId,
            dogNames: item?.dogNames ?? [],
            onConfirm: () => checkInRef.current?.(bookingId, true),
          });
          return;
        }
        const message = err instanceof Error ? err.message : 'Failed to check-in';
        setActionError(message);
        // Auto-clear after 5s so the row doesn't get stuck in error chrome.
        setTimeout(() => setActionError(null), 5000);
      } finally {
        setLoading([bookingId], false);
      }
    },
    [apiUrl, token, onMutation, setLoading, data, askArrival, askInterrupt, offerRunCards, offerOwnerNotes],
  );
  checkInRef.current = handleCheckIn;

  // Ted #2 (#905) — check in every dog of one household with one tap. Each
  // booking gets its own POST (the existing route, unchanged); all of them
  // settle before anything else happens, so Skipper being already checked
  // in never stops Hazel and Hank. Then ONE refresh reconciles the list
  // from source, and the banner names exactly who did not go through.
  const handleCheckInHousehold = useCallback(
    async (
      rows: DashboardArrival[],
      decisions?: ReadonlyMap<string, InterruptDecision>,
      /** Lane LC — the dogs the step stopped, named on the same banner as the batch's refusals. */
      stoppedNote: string | null = null,
    ) => {
      // Lane LC — the household's UNION of check-in alerts, once, before any
      // POST; a Do Not Admit dog is named and left out, the siblings go in.
      if (!decisions) {
        const plan = planCheckInInterrupt(rows);
        if (plan.needed) {
          askInterrupt({
            key: `household:${rows[0]?.customerId ?? rows.map((r) => r.bookingId).join(',')}`,
            plan,
            onProceed: (result) => {
              const stopped = describeStopped(result.stopped);
              const map = new Map(result.proceed.map((d) => [d.bookingId, d] as const));
              const go = rows.filter((r) => map.has(r.bookingId));
              if (go.length > 0) {
                void handleCheckInHousehold(go, map, stopped);
              } else if (stopped) {
                setActionError(stopped);
                setTimeout(() => setActionError(null), 8000);
              }
            },
          });
          return;
        }
      }
      const ids = rows.map((r) => r.bookingId);
      setLoading(ids, true);
      setActionError(null);
      const outcome = await checkInHousehold(rows, (row) => {
        const d = decisions?.get(row.bookingId);
        return checkInBooking(apiUrl, token, row.bookingId, undefined, d?.arrivalVerified, d?.heatConfirmed);
      });
      // Ted Item 34 — one prompt for the whole family's cards.
      if (outcome.succeeded.length > 0) offerRunCards(outcome.succeeded.map((r) => r.bookingId));
      // Sawyer #1296 — ONE owner's-notes question for the family's notes.
      if (outcome.succeeded.length > 0) offerOwnerNotes(outcome.succeeded.map(factsFromArrival));
      // Ted #28 (#1050) — THE BATCH RULE. "Check in all" never sweeps a
      // transported dog in; each one gets its own question, in turn, while
      // its walk-in siblings are already checked in.
      for (const row of outcome.needsArrivalCheck) {
        askArrival({
          bookingId: row.bookingId,
          dogNames: row.dogNames,
          onConfirm: () => checkInRef.current?.(row.bookingId, true),
        });
      }
      const message = [stoppedNote, summarizeHouseholdCheckIn(outcome)].filter(Boolean).join(' ');
      if (message) {
        setActionError(message);
        setTimeout(() => setActionError(null), stoppedNote ? 8000 : 5000);
      }
      setLoading(ids, false);
      // One refresh for the whole household — even when a dog was refused,
      // the refusal ("already checked in") means the list was stale.
      onMutation?.();
    },
    [apiUrl, token, onMutation, setLoading, askArrival, askInterrupt, offerRunCards, offerOwnerNotes],
  );

  // Ted #32c (#1160) — Check In on a row. A dog whose family has others still
  // to arrive asks first, with ONLY this dog going in and the siblings
  // unticked; a dog with no sibling checks in with one tap, as before.
  const requestCheckIn = (bookingId: string): void => {
    const arrivals = data?.arrivals ?? [];
    const row = arrivals.find((a) => a.bookingId === bookingId);
    const siblings = row ? siblingsStillToArrive(row, arrivals) : [];
    if (!row || siblings.length === 0) {
      void handleCheckIn(bookingId);
      return;
    }
    setFamily({ anchor: row, siblings, startTicked: 'anchor-only' });
  };

  // "Check in all (N)" asks too: the same picker, family ticked because the
  // desk asked for all of them, any dog that is not here yet one tap from off.
  const requestCheckInAll = (rows: DashboardArrival[]): void => {
    if (rows.length === 0) return;
    setFamily({ anchor: null, siblings: rows, startTicked: 'all' });
  };

  // The picker's answer: one id is a plain check-in, several are the batch.
  const confirmFamily = (ids: readonly string[]): void => {
    const picked = family
      ? [family.anchor, ...family.siblings].filter((r): r is DashboardArrival => r !== null)
      : [];
    setFamily(null);
    const rows = ids
      .map((id) => picked.find((r) => r.bookingId === id))
      .filter((r): r is DashboardArrival => r !== undefined);
    if (rows.length === 0) return;
    if (rows.length === 1) void handleCheckIn(rows[0]!.bookingId);
    else void handleCheckInHousehold(rows);
  };

  // #251 should-fix 5 — check-out routes through the Bookings page's
  // money-path orchestrator (two-track choice, package redemption,
  // open-invoice record, settle-now dialog). The widget never POSTs
  // check-out directly; that bypass silently exited the money funnel.
  const handleCheckOut = useCallback(
    (bookingId: string) => {
      navigate(checkOutPath(bookingId));
    },
    [navigate],
  );

  const arrivals = data?.arrivals ?? [];
  const departures = data?.departures ?? [];
  // A row is busy while its check-in OR a Cancel / Edit / No-show is in flight.
  const busyIds = new Set<string>([...actionLoading, ...reservation.busyIds]);
  const today = todayIsoDate();
  // Ted #23 (#984) — the tab badges count VISITS, not booking rows.
  const arrivalVisits = countVisits(arrivals);
  const departureVisits = countVisits(departures);

  return (
    <article
      className="ferro-dashboard-widget"
      aria-label="Today's arrivals and departures"
      data-testid="arrivals-departures"
    >
      {/* Sawyer 11 client-notes-agreements (F1) — one signals read for today's rows; refetches with /today. */}
      <ClientNoteSignalsProvider apiUrl={apiUrl} customerIds={[...arrivals, ...departures].map((r) => r.customerId)} refreshKey={data}>
      {/* Ted #28 (#1050) — "Is Bella here?" for a transported arrival. */}
      {arrivalDialog}
      {/* Ted Item 34 — "Print the run card?" after a check-in. */}
      {runCards.element}
      {/* Sawyer #1296 — "Add this to the owner's notes?" after a check-in. */}
      {ownerNotes.element}
      {/* Lane LC — "Before you check in" (flags, Do Not Admit stop, heat question). */}
      {interruptDialog}
      {/* Ted #32c (#1158) — the Cancel / Edit / No-show dialog, one at a time. */}
      {reservation.dialogs}
      {/* Ted #32c (#1160) — "who else is here?", siblings unticked by default. */}
      {family ? (
        <FamilyCheckInDialog
          key={family.anchor?.bookingId ?? 'all'}
          anchor={family.anchor ? familyMemberFromArrival(family.anchor) : null}
          siblings={family.siblings.map(familyMemberFromArrival)}
          ownerLabel={(family.anchor ?? family.siblings[0])?.customerName ?? 'this family'}
          startTicked={family.startTicked}
          onClose={() => setFamily(null)}
          onConfirm={confirmFamily}
        />
      ) : null}
      <header className="ferro-dashboard-widget__header">
        <h2 className="ferro-section__subtitle">Arrivals &amp; Departures</h2>
        {isLoading && (
          <div
            className="ferro-dashboard-widget__spinner"
            role="status"
            aria-label="Loading today's bookings"
          />
        )}
      </header>

      {error && (
        <div className="ferro-dashboard-widget__error" role="alert">
          <span>{error}</span>
          {onRetry ? (
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={onRetry}
            >
              Retry
            </button>
          ) : null}
        </div>
      )}

      {actionError && (
        <div
          className="ferro-dashboard-widget__error ferro-dashboard-widget__error-action"
          role="alert"
        >
          <span>{actionError}</span>
          <button
            type="button"
            onClick={() => setActionError(null)}
            className="ferro-arrivals__close"
            aria-label="Dismiss error"
          >
            <svg
              className="ferro-arrivals__close-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Ted #32c (#1158) — what a Cancel / Edit / No-show just did. */}
      {reservation.banner}

      {!data && isLoading && (
        <div
          className="ferro-arrivals__skeleton"
          data-testid="arrivals-departures-skeleton"
        >
          <div className="ferro-arrivals__skeleton-row" />
          <div className="ferro-arrivals__skeleton-row" />
          <div className="ferro-arrivals__skeleton-row" />
        </div>
      )}

      {data && (
        <>
          {/* Mobile tab switch */}
          <div
            className="ferro-arrivals__tabs"
            role="tablist"
            aria-label="Arrivals or departures"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'arrivals'}
              onClick={() => setActiveTab('arrivals')}
              className={`ferro-arrivals__tab${activeTab === 'arrivals' ? ' ferro-arrivals__tab--active' : ''}`}
            >
              Arrivals
              {arrivalVisits > 0 && (
                <span className="ferro-arrivals__tab-count">
                  {arrivalVisits}
                </span>
              )}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'departures'}
              onClick={() => setActiveTab('departures')}
              className={`ferro-arrivals__tab${activeTab === 'departures' ? ' ferro-arrivals__tab--active' : ''}`}
            >
              Departures
              {departureVisits > 0 && (
                <span className="ferro-arrivals__tab-count">
                  {departureVisits}
                </span>
              )}
            </button>
          </div>

          {/* Desktop two-column */}
          <div className="ferro-arrivals__desktop" data-testid="desktop-layout">
            <ArrivalsColumn
              arrivals={arrivals}
              actionLoading={busyIds}
              onCheckIn={requestCheckIn}
              onCheckInHousehold={requestCheckInAll}
              onEdit={(item) => reservation.edit(reservationTargetFromArrival(item, today))}
              onNoShow={(item) => reservation.noShow(reservationTargetFromArrival(item, today))}
              onCancel={(item) => reservation.cancel(reservationTargetFromArrival(item, today))}
              dataSuspect={dataSuspect}
              testIdPrefix=""
              apiUrl={apiUrl}
            />
            <DeparturesColumn
              departures={departures}
              onCheckOut={handleCheckOut}
              onRunCard={runCards.printCards}
              runCardBusy={runCards.active}
              dataSuspect={dataSuspect}
              testIdPrefix=""
              apiUrl={apiUrl}
            />
          </div>

          {/* Mobile single column — separate testid prefix so jsdom tests can
              target the desktop layout deterministically (jsdom ignores
              CSS so both layouts render to the DOM in tests). */}
          <div className="ferro-arrivals__mobile" data-testid="mobile-layout">
            {activeTab === 'arrivals' && (
              <ArrivalsColumn
                arrivals={arrivals}
                actionLoading={busyIds}
                onCheckIn={requestCheckIn}
                onCheckInHousehold={requestCheckInAll}
                onEdit={(item) => reservation.edit(reservationTargetFromArrival(item, today))}
                onNoShow={(item) => reservation.noShow(reservationTargetFromArrival(item, today))}
                onCancel={(item) => reservation.cancel(reservationTargetFromArrival(item, today))}
                dataSuspect={dataSuspect}
                hideHeader
                testIdPrefix="mobile-"
                apiUrl={apiUrl}
              />
            )}
            {activeTab === 'departures' && (
              <DeparturesColumn
                departures={departures}
                onCheckOut={handleCheckOut}
                onRunCard={runCards.printCards}
                runCardBusy={runCards.active}
                dataSuspect={dataSuspect}
                hideHeader
                testIdPrefix="mobile-"
                apiUrl={apiUrl}
              />
            )}
          </div>
        </>
      )}
      </ClientNoteSignalsProvider>
    </article>
  );
}

/**
 * Empty-state copy honest about WHY the list is empty (Principle 5):
 * a structural zero (sync dead / nothing imported) must never read as
 * a quiet day. The stale variant reuses the hero's honesty vocabulary.
 */
function EmptyRow({
  dataSuspect,
  normalCopy,
}: {
  dataSuspect: boolean;
  normalCopy: string;
}): JSX.Element {
  const { t } = useTranslation();
  if (dataSuspect) {
    return (
      <li
        className="ferro-arrivals__empty ferro-arrivals__empty--stale"
        data-testid="arrivals-honest-empty"
      >
        {t('admin.dashboard.hero.scheduleNotInSystem')}
      </li>
    );
  }
  return <li className="ferro-arrivals__empty">{normalCopy}</li>;
}

/**
 * #545/#546 — dog-first row body. Leads with the booked dog(s) via the
 * shared DogFirstCell (photo/name/breed + status strip, owner secondary),
 * falling back to the legacy owner-led text when a stale server omits the
 * `dogs` payload (deploy-order safety). Service + expected time trail below.
 */
function DogFirstItemBody({
  item,
  apiUrl,
}: {
  item: DashboardArrival | DashboardDeparture;
  /** #581-followup — passed to DogFirstCell so dog names open the pet card. */
  apiUrl: string;
}): JSX.Element {
  const hasDogCards = Array.isArray(item.dogs) && item.dogs.length > 0;
  return (
    <>
      {hasDogCards ? (
        <DogFirstCell
          dogs={item.dogs!}
          ownerName={item.customerName}
          customerId={item.customerId}
          unpaid={item.unpaid}
          balanceDueCents={item.balanceDueCents}
          // Ted #7 (#910) — store credit on the row, before the cart.
          storeCreditCents={item.walletBalanceCents}
          apiUrl={apiUrl}
          ownerAddon={<ClientNoteChip variant="link" customerId={item.customerId} />}
        />
      ) : (
        <>
          <p className="ferro-arrivals__item-name"><OwnerLink name={item.customerName} customerId={item.customerId} /></p>
          <p className="ferro-arrivals__item-meta">
            {item.dogNames.join(', ')}
          </p>
          {/* Sawyer 11 (F1, refuter C11) — the chip survives a stale payload. */}
          <ClientNoteChip variant="link" customerId={item.customerId} />
        </>
      )}
      <p className="ferro-arrivals__item-meta">
        {/* Ted #17 (#921) — a small chip in the service's owner-chosen color,
            the customer-facing name on it; an older server (no
            serviceDisplayName / serviceColor) reads neutral with the engine key. */}
        <span
          className={['ferro-service-chip', serviceColorClass(item.serviceColor)].filter(Boolean).join(' ')}
          data-testid={`arrival-service-${item.bookingId}`}
          data-color={item.serviceColor ?? ''}
        >
          {/* #984 — a leg reads as its add-on ("Nail Trim"), never as the
              service type it rides on ("Grooming"). */}
          {serviceRowLabel(item)}
        </span>
        {item.expectedTime ? ` · ${formatTime(item.expectedTime)}` : ''}
      </p>
    </>
  );
}

/**
 * Ted #23 (#984) — the line that says a row is an added service on a visit,
 * so the desk never reads it as a second dog arriving or leaving.
 */
function LegTag({
  item,
  testId,
}: {
  item: DashboardArrival | DashboardDeparture;
  testId: string;
}): JSX.Element {
  return (
    <p className="ferro-arrivals__leg-tag" data-testid={testId}>
      Added service on this visit
      {item.needsSpecialist ? ' · needs a specialist' : ''}
    </p>
  );
}

/**
 * Ted #23 (#984) — what a leg row shows WHERE a lifecycle button would be.
 * Deliberately text, not a disabled button: there is nothing to tap, and a
 * per-leg check-in / check-out moved the nail trim without moving the visit.
 */
function LegRides({ copy, testId }: { copy: string; testId: string }): JSX.Element {
  return (
    <span className="ferro-arrivals__leg-rides" data-testid={testId}>
      {copy}
    </span>
  );
}

interface ColumnProps {
  arrivals: DashboardArrival[];
  /** Booking ids with a check-in in flight (a household puts all of its rows here). */
  actionLoading: ReadonlySet<string>;
  onCheckIn: (bookingId: string) => void;
  /** Ted #2 (#905) — check in every row of one household together. */
  onCheckInHousehold: (rows: DashboardArrival[]) => void;
  /** Ted #32c (#1158) — the Expected row's own reservation actions. */
  onEdit: (item: DashboardArrival) => void;
  onNoShow: (item: DashboardArrival) => void;
  onCancel: (item: DashboardArrival) => void;
  dataSuspect: boolean;
  hideHeader?: boolean;
  testIdPrefix: string;
  apiUrl: string;
}

function ArrivalsColumn({
  arrivals,
  actionLoading,
  onCheckIn,
  onCheckInHousehold,
  onEdit,
  onNoShow,
  onCancel,
  dataSuspect,
  hideHeader,
  testIdPrefix,
  apiUrl,
}: ColumnProps): JSX.Element {
  // Ted #2 (#905) — same-owner rows sit together; the header renders only
  // when there are at least two of them to check in as one.
  const segments = useMemo(() => segmentByHousehold(arrivals), [arrivals]);

  const renderRow = (item: DashboardArrival): JSX.Element => {
    const loading = actionLoading.has(item.bookingId);
    return (
      <li
        key={item.bookingId}
        className="ferro-arrivals__item ferro-arrivals__item--arrival"
        data-testid={`${testIdPrefix}arrival-${item.bookingId}`}
      >
        <div className="ferro-arrivals__item-body">
          <DogFirstItemBody item={item} apiUrl={apiUrl} />
          {/* Sawyer 03 today-notes-board (C2, G8) — read-only on the Dashboard
              (every role sees it; the desk writes from Front Desk). */}
          {item.isLeg === true ? null : (
            <BookingNoteCallout
              bookingNote={item.bookingNote}
              deskNotes={deskNotesOf(item)}
              testIdPrefix={`${testIdPrefix}arrival-note-${item.bookingId}`}
            />
          )}
          {item.isLeg === true ? (
            <LegTag item={item} testId={`${testIdPrefix}arrival-leg-tag-${item.bookingId}`} />
          ) : (
            // Ted #32c (#1158) — Gingr's Expected row: the X cancels, the other
            // options edit. Never on a leg: it follows its visit.
            <ExpectedRowActions
              bookingId={item.bookingId}
              dogLabel={item.dogNames.join(', ') || item.customerName}
              testIdPrefix={`${testIdPrefix}arrival`}
              disabled={loading}
              onEdit={() => onEdit(item)}
              onNoShow={() => onNoShow(item)}
              onCancel={() => onCancel(item)}
            />
          )}
        </div>
        {item.isLeg === true ? (
          <LegRides
            copy="Checks in with the visit"
            testId={`${testIdPrefix}arrival-leg-rides-${item.bookingId}`}
          />
        ) : (
          <button
            type="button"
            onClick={() => onCheckIn(item.bookingId)}
            disabled={loading}
            className="ferro-arrivals__action ferro-arrivals__action--in"
            data-testid={`${testIdPrefix}check-in-${item.bookingId}`}
            aria-label={`Check in ${item.customerName}`}
          >
            {loading ? '...' : 'Check In'}
          </button>
        )}
      </li>
    );
  };

  return (
    <section data-testid={`${testIdPrefix}arrivals-column`}>
      {!hideHeader && (
        <div className="ferro-arrivals__column-header">
          <ArrowInIcon />
          <span>Arrivals</span>
          <span className="ferro-arrivals__column-pill ferro-arrivals__column-pill--arrival">
            {countVisits(arrivals)}
          </span>
        </div>
      )}
      <ul className="ferro-arrivals__list">
        {arrivals.length === 0 && (
          <EmptyRow dataSuspect={dataSuspect} normalCopy="No arrivals scheduled" />
        )}
        {segments.map((segment) => {
          // Ted #23 (#984) — a household is a family of VISITS. A leg carries
          // its visit's owner and dog, so counted as a member it turned one
          // dog with a nail trim into a "family" of two and put the leg in the
          // bulk POST. Legs still render inside the card, under their family.
          const visits = segment.rows.filter((r) => r.isLeg !== true);
          if (segment.customerId === null || visits.length < 2) {
            return segment.rows.map(renderRow);
          }
          const dogs = joinDogNames(householdDogNames(visits));
          const busy = visits.some((r) => actionLoading.has(r.bookingId));
          return (
            <li
              key={`household-${segment.customerId}`}
              className="ferro-arrivals__household"
              data-testid={`${testIdPrefix}household-${segment.customerId}`}
            >
              <div className="ferro-arrivals__household-head">
                <p className="ferro-arrivals__household-title">
                  <span className="ferro-arrivals__household-dogs">{dogs}</span>
                  <span className="ferro-arrivals__household-owner">
                    {' — '}
                    <OwnerLink name={segment.customerName} customerId={segment.customerId} />
                  </span>
                </p>
                <button
                  type="button"
                  onClick={() => onCheckInHousehold(visits)}
                  disabled={busy}
                  className="ferro-arrivals__action ferro-arrivals__action--in ferro-arrivals__action--household"
                  data-testid={`${testIdPrefix}check-in-household-${segment.customerId}`}
                  aria-label={`Check in all ${visits.length}: ${dogs} — ${segment.customerName}`}
                >
                  {busy ? '...' : `Check in all (${visits.length})`}
                </button>
              </div>
              <ul className="ferro-arrivals__household-rows">
                {segment.rows.map(renderRow)}
              </ul>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

interface DepartureColumnProps {
  departures: DashboardDeparture[];
  onCheckOut: (bookingId: string) => void;
  /** Sawyer 10-30 — print this visit's card by hand (any service). */
  onRunCard: (bookingIds: ReadonlyArray<string>) => void;
  /** A card is loading or the prompt is open: the row's button waits. */
  runCardBusy: boolean;
  dataSuspect: boolean;
  hideHeader?: boolean;
  testIdPrefix: string;
  apiUrl: string;
}

function DeparturesColumn({
  departures,
  onCheckOut,
  onRunCard,
  runCardBusy,
  dataSuspect,
  hideHeader,
  testIdPrefix,
  apiUrl,
}: DepartureColumnProps): JSX.Element {
  return (
    <section data-testid={`${testIdPrefix}departures-column`}>
      {!hideHeader && (
        <div className="ferro-arrivals__column-header">
          <ArrowOutIcon />
          <span>Departures</span>
          <span className="ferro-arrivals__column-pill ferro-arrivals__column-pill--departure">
            {countVisits(departures)}
          </span>
        </div>
      )}
      <ul className="ferro-arrivals__list">
        {departures.length === 0 && (
          <EmptyRow
            dataSuspect={dataSuspect}
            normalCopy="No departures scheduled"
          />
        )}
        {departures.map((item) => (
          <li
            key={item.bookingId}
            className="ferro-arrivals__item ferro-arrivals__item--departure"
            data-testid={`${testIdPrefix}departure-${item.bookingId}`}
          >
            <div className="ferro-arrivals__item-body">
              <DogFirstItemBody item={item} apiUrl={apiUrl} />
              {/* Sawyer 03 today-notes-board (C2, G8; refuter C-1) — a checked-in
                  daycare dog sits here all day; its notes stay with it. */}
              {item.isLeg === true ? null : (
                <BookingNoteCallout
                  bookingNote={item.bookingNote}
                  deskNotes={deskNotesOf(item)}
                  testIdPrefix={`${testIdPrefix}departure-note-${item.bookingId}`}
                />
              )}
              {item.isLeg === true ? (
                <LegTag
                  item={item}
                  testId={`${testIdPrefix}departure-leg-tag-${item.bookingId}`}
                />
              ) : (
                // Sawyer 10-30 — every visit's card by hand; a leg has none.
                <div>
                  <button
                    type="button"
                    className="ferro-btn ferro-btn--ghost ferro-arrivals__run-card"
                    onClick={() => onRunCard([item.bookingId])}
                    disabled={runCardBusy}
                    data-testid={`${testIdPrefix}run-card-${item.bookingId}`}
                    aria-label={`Print the run card for ${item.dogNames.join(', ') || item.customerName}`}
                  >
                    Run card
                  </button>
                </div>
              )}
            </div>
            {/* #984 — a leg settles on its visit's checkout. A per-leg Check
                Out would open the money path for the nail trim alone and
                leave the visit's invoice behind. */}
            {item.isLeg === true ? (
              <LegRides
                copy="Checks out with the visit"
                testId={`${testIdPrefix}departure-leg-rides-${item.bookingId}`}
              />
            ) : (
              <button
                type="button"
                onClick={() => onCheckOut(item.bookingId)}
                className="ferro-arrivals__action ferro-arrivals__action--out"
                data-testid={`${testIdPrefix}check-out-${item.bookingId}`}
                aria-label={`Check out ${item.customerName} on the Bookings page`}
              >
                Check Out
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function ArrowInIcon(): JSX.Element {
  return (
    <svg
      className="ferro-arrivals__icon ferro-arrivals__icon--in"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
      />
    </svg>
  );
}

function ArrowOutIcon(): JSX.Element {
  return (
    <svg
      className="ferro-arrivals__icon ferro-arrivals__icon--out"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
      />
    </svg>
  );
}
